import React, { useState, useRef, useCallback, useEffect } from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Plus, WandSparkles, HelpCircle, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import GradientButtonCentered from '@/components/ui/GradientButtonCentered';
import { Textarea } from '@/components/ui/textarea';
import GlassPill from '@/components/ui/GlassPill';
import { RelatedQuestionItem } from './RelatedQuestionItem';
import { useRelatedQuestionGeneration } from '@/hooks/research-generator/related-questions/useRelatedQuestionGeneration';
import { usePrivateResearchQuestionGeneration } from '@/hooks/research-generator/related-questions/usePrivateResearchQuestionGeneration';
import { useRelatedQuestionUpdate } from '@/hooks/research-generator/related-questions/useRelatedQuestionUpdate';
import { useAddRelatedQuestion } from '@/hooks/research-generator/related-questions/useAddRelatedQuestion';
import { useAddPrivateRelatedQuestion } from '@/hooks/research-generator/related-questions/useAddPrivateRelatedQuestion';
import { RelatedQuestionItem as RelatedQuestionItemType } from '@/hooks/research-generator/related-questions/types';

interface RelatedQuestionsSectionProps {
  relatedQuestions?: any[];
  onRelatedQuestionClick?: (question: string) => void;
  currentResearchId?: string;
  currentResearchParameters?: Record<string, any>;
  onGenerationStateChange?: (isGenerating: boolean) => void;
  contentType?: 'research' | 'private_research'; // NEW: Optional prop for content type
}

// Custom ResearchPromptField with custom placeholder
interface CustomResearchPromptFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const CustomResearchPromptField: React.FC<CustomResearchPromptFieldProps> = React.memo(({
  value,
  onChange,
  placeholder = "What would you like to research?",
  disabled = false
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    const cursorPosition = e.target.selectionStart;
    
    // Update value
    onChange(newValue);
    
    // Preserve cursor position after state update
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.setSelectionRange(cursorPosition, cursorPosition);
        textareaRef.current.focus();
      }
    }, 0);
  }, [onChange]);

  const wordCount = value.trim().split(/\s+/).filter(word => word.length > 0).length;
  const isValidLength = wordCount >= 5;

  return (
    <div className="space-y-3">
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={4}
        className="resize-none border-orange-200/50 dark:border-orange-800/50 focus-visible:border-orange-400/60 dark:focus-visible:border-orange-500/60 hover:border-orange-300/60 dark:hover:border-orange-700/60"
        disabled={disabled}
        autoFocus={true}
      />
      
      <div className="flex items-center gap-2 mt-1">
        {wordCount < 5 && (
          <GlassPill 
            type="Min 5 words required"
            variant="amber"
            className="text-xs py-0.5 px-2"
          />
        )}
        <GlassPill 
          type={`${wordCount} words`}
          variant={isValidLength ? "purple" : "default"}
          className="text-xs py-0.5 px-2"
        />
        {isValidLength && (
          <GlassPill 
            type="Ready to generate!"
            variant="green"
            className="text-xs py-0.5 px-2"
          />
        )}
      </div>
    </div>
  );
});

CustomResearchPromptField.displayName = 'CustomResearchPromptField';

export const RelatedQuestionsSection: React.FC<RelatedQuestionsSectionProps> = ({ 
  relatedQuestions = [],
  onRelatedQuestionClick,
  currentResearchId,
  currentResearchParameters = {},
  onGenerationStateChange,
  contentType = 'research' // Default to 'research' for backward compatibility
}) => {
  const [generatingIndex, setGeneratingIndex] = useState<number | null>(null);
  const [showNewQuestionInput, setShowNewQuestionInput] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState('');
  const [isGeneratingNewQuestion, setIsGeneratingNewQuestion] = useState(false);
  
  // Use the appropriate hook based on content type
  const publicResearchGeneration = useRelatedQuestionGeneration();
  const privateResearchGeneration = usePrivateResearchQuestionGeneration();
  
  // Select the correct research generation hook based on content type
  const { generateResearchFromQuestion, isGenerating } = contentType === 'private_research' 
    ? privateResearchGeneration 
    : publicResearchGeneration;
  
  // Select the correct add question hook based on content type
  const publicAddQuestion = useAddRelatedQuestion();
  const privateAddQuestion = useAddPrivateRelatedQuestion();
  
  const { addRelatedQuestion, isAdding } = contentType === 'private_research' 
    ? privateAddQuestion 
    : publicAddQuestion;
  
  const { updateRelatedQuestion } = useRelatedQuestionUpdate();

  // Check if any research generation is happening
  const isAnyGenerationActive = isGenerating || isGeneratingNewQuestion || generatingIndex !== null;

  // Auto-close new question form when generation starts
  useEffect(() => {
    if (isAnyGenerationActive && showNewQuestionInput) {
      setShowNewQuestionInput(false);
      setNewQuestionText('');
    }
  }, [isAnyGenerationActive, showNewQuestionInput]);

  // Convert any format to RelatedQuestionItem array
  const normalizeRelatedQuestions = (questions: any[]): RelatedQuestionItemType[] => {
    if (!Array.isArray(questions)) return [];
    
    return questions.map(item => {
      // If it's already in the correct format, return as-is
      if (typeof item === 'object' && item !== null && 'question' in item && 'research_id' in item) {
        return item as RelatedQuestionItemType;
      }
      // If it's a string, return as string
      if (typeof item === 'string') {
        return item;
      }
      // Fallback to empty string for unexpected formats
      return '';
    });
  };

  const normalizedRelatedQuestions = normalizeRelatedQuestions(relatedQuestions);

  const handleGenerateResearch = async (question: string, index: number) => {
    if (!currentResearchId || isGenerating || generatingIndex !== null) {
      return;
    }

    setGeneratingIndex(index);
    // Notify parent that generation started
    onGenerationStateChange?.(true);

    try {
      console.log('[RelatedQuestionsSection] Generating research for question:', question);
      
      const generatedResearchId = await generateResearchFromQuestion({
        question,
        researchParameters: currentResearchParameters,
        currentResearchId
      });

      if (generatedResearchId) {
        console.log('[RelatedQuestionsSection] Research generated, updating database:', generatedResearchId);
        
        // Update the related questions in the database
        updateRelatedQuestion({
          researchId: currentResearchId,
          questionIndex: index,
          generatedResearchId,
          originalQuestion: question,
          contentType // Pass the content type to determine which table to update
        });
      }
    } catch (error) {
      console.error('[RelatedQuestionsSection] Error in generation flow:', error);
    } finally {
      setGeneratingIndex(null);
      // Notify parent that generation stopped
      onGenerationStateChange?.(false);
    }
  };

  const handleGenerateNewQuestion = async () => {
    if (!currentResearchId || !newQuestionText.trim() || isGeneratingNewQuestion) {
      return;
    }

    setIsGeneratingNewQuestion(true);
    onGenerationStateChange?.(true);

    try {
      console.log('[RelatedQuestionsSection] Adding new question to database:', newQuestionText);
      
      // Store the question text for later use
      const questionText = newQuestionText.trim();
      
      // Add the question to the database (this will add it as a string at the top of the array)
      addRelatedQuestion({
        researchId: currentResearchId,
        questionText: questionText
      });

      console.log('[RelatedQuestionsSection] New question added to database');
      
      // Reset the form
      setNewQuestionText('');
      setShowNewQuestionInput(false);
      
      // Wait a brief moment for the database update to complete and queries to invalidate
      setTimeout(async () => {
        console.log('[RelatedQuestionsSection] Auto-generating research for new question');
        
        // Automatically trigger research generation for the new question at index 0
        await handleGenerateResearch(questionText, 0);
      }, 100);
      
    } catch (error) {
      console.error('[RelatedQuestionsSection] Error adding new question:', error);
    } finally {
      setIsGeneratingNewQuestion(false);
      onGenerationStateChange?.(false);
    }
  };

  const handleNavigateToResearch = (researchId: string) => {
    // Use the existing research selection logic directly
    console.log('[RelatedQuestionsSection] Navigating to research via onRelatedQuestionClick:', researchId);
    if (onRelatedQuestionClick) {
      onRelatedQuestionClick(researchId);
    }
  };

  const handleCancelNewQuestion = () => {
    setShowNewQuestionInput(false);
    setNewQuestionText('');
  };

  // Dual-state button handler
  const handleDualStateButtonClick = () => {
    if (showNewQuestionInput) {
      // Form is open - act as Generate Research button
      handleGenerateNewQuestion();
    } else {
      // Form is closed - act as New Question button
      setShowNewQuestionInput(true);
    }
  };

  // Check if we can generate (form is open and has valid input)
  const canGenerate = showNewQuestionInput && newQuestionText.trim().split(/\s+/).filter(word => word.length > 0).length >= 5;

  // Single return with all cases handled smoothly
  return (
    <TooltipProvider>
      <div className="space-y-3">
        {/* New Question Form - Appears above the button */}
        <AnimatePresence>
          {showNewQuestionInput && (
            <motion.div
              key="new-question-form"
              initial={{ 
                scale: 0.95, 
                height: 0, 
                opacity: 0,
                y: -20
              }}
              animate={{ 
                scale: 1, 
                height: "auto", 
                opacity: 1,
                y: 0,
                transition: {
                  duration: 0.35,
                  ease: "easeOut",
                  height: { duration: 0.25, ease: "easeInOut" }
                }
              }}
              exit={{ 
                scale: 0.95, 
                height: 0, 
                opacity: 0,
                y: -10,
                transition: { 
                  duration: 0.25,
                  ease: "easeInOut"
                }
              }}
              className="space-y-3 overflow-hidden"
            >
              <motion.div 
                className="flex items-center justify-between"
                initial={{ y: -10, opacity: 0 }}
                animate={{ 
                  y: 0, 
                  opacity: 1,
                  transition: { delay: 0.15, duration: 0.2 }
                }}
                exit={{ 
                  y: -5, 
                  opacity: 0,
                  transition: { duration: 0.15 }
                }}
              >
                <div className="flex items-center gap-2">
                  <motion.div
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ 
                      scale: 1, 
                      rotate: 0,
                      transition: { 
                        delay: 0.25, 
                        type: "spring", 
                        stiffness: 400, 
                        damping: 15 
                      }
                    }}
                    exit={{ 
                      scale: 0, 
                      rotate: 180,
                      transition: { duration: 0.15 }
                    }}
                  >
                    <HelpCircle size={16} className="text-orange-600 dark:text-orange-400" />
                  </motion.div>
                  <span className="text-sm font-medium text-orange-700 dark:text-orange-300">Enter your question</span>
                </div>
                <motion.button
                  onClick={handleCancelNewQuestion}
                  disabled={isGeneratingNewQuestion}
                  initial={{ scale: 0, rotate: -90 }}
                  animate={{ 
                    scale: 1, 
                    rotate: 0,
                    transition: { 
                      delay: 0.2, 
                      type: "spring", 
                      stiffness: 400, 
                      damping: 15 
                    }
                  }}
                  exit={{ 
                    scale: 0, 
                    rotate: 90,
                    transition: { duration: 0.15 }
                  }}
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  className="p-1 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200 disabled:opacity-50"
                  title="Cancel"
                >
                  <X size={16} />
                </motion.button>
              </motion.div>
              
              <motion.div
                initial={{ y: 10, opacity: 0, scale: 0.98 }}
                animate={{ 
                  y: 0, 
                  opacity: 1, 
                  scale: 1,
                  transition: { delay: 0.2, duration: 0.25 }
                }}
                exit={{ 
                  y: 5, 
                  opacity: 0, 
                  scale: 0.98,
                  transition: { duration: 0.15 }
                }}
              >
                <CustomResearchPromptField
                  value={newQuestionText}
                  onChange={setNewQuestionText}
                  placeholder="What would you like to research?"
                  disabled={isGeneratingNewQuestion}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Dual-State Button - Always visible, transforms based on state */}
        <div className="flex justify-center">
          <GradientButtonCentered 
            onClick={handleDualStateButtonClick}
            disabled={isAnyGenerationActive || (showNewQuestionInput && !canGenerate)}
            variant="purple"
            icon={<WandSparkles className="h-5 w-5" />}
            isLoading={isGeneratingNewQuestion}
            fullWidth={true}
            testId={showNewQuestionInput ? "generate-research-button" : "new-question-button"}
          >
            {isGeneratingNewQuestion 
              ? 'Generating Research...' 
              : showNewQuestionInput 
                ? 'Generate Research' 
                : 'New Question'
            }
          </GradientButtonCentered>
        </div>

        {/* Existing Related Questions */}
        {normalizedRelatedQuestions.length > 0 && (
          <div className="space-y-2">
            {normalizedRelatedQuestions.map((question, index) => (
              <div
                key={typeof question === 'string' ? `question-${question.slice(0, 50)}` : `research-${question.research_id || index}`}
                className={`${
                  isAnyGenerationActive && generatingIndex !== index 
                    ? 'opacity-80 pointer-events-none' 
                    : ''
                }`}
              >
                <RelatedQuestionItem
                  question={question}
                  index={index}
                  isGenerating={generatingIndex === index}
                  onGenerate={handleGenerateResearch}
                  onNavigate={handleNavigateToResearch}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}; 